import { Vec3 } from '../../engine/math/vec3';
import { wrapAngle } from '../../engine/math/easing';
import type { Game } from '../Game';
import type { ItemId } from '../items/ItemDefs';
import type { BeastKind } from '../beasts/Beast';
import { SPOTS } from '../world/Layout';
import { MARSH_DEADLY_Y } from '../world/Terrain';

/**
 * §8.8 route bot: plays the Mode A critical path through the same input layer a human uses
 * (virtual WASD/Shift/Space/E/F). Navigation is a hand-authored node graph plus the maze's BFS.
 * It is the acceptance test: `game.debug.runCriticalPath()` then wait for `game.world.won`.
 */

type Node = { id: string; p: Vec3 };
const N = (id: string, x: number, z: number): Node => ({ id, p: new Vec3(x, 0, z) });

const NODES: Node[] = [
  N('spawn', 0, 14), N('circle', 24, 24), N('gateOut', 0, 7), N('goldDais', 0, -9.6), N('keepE', 22, 4), N('keepNE', 22, -20),
  N('hubW', -50, 0), N('corrW', -100, 0), N('shore', -150, 0),
  N('wade1', -172, 12), N('nearIsland', -196, 22), N('wade2', -198, 4), N('wade3', -205, -4), N('neckE', -216.2, -6.4), N('neckW', -220.8, -0.4), N('farBank', -232, -2), N('farIsland', -232, -26), N('shrine', -238, -30.5),
  N('hubN', 0, -50), N('corrN', 0, -100), N('dunesIn', 0, -140), N('whiteKey', -31, -190), N('cageOut', 8, -215.5),
  N('hubE', 50, 0), N('corrE', 100, 0), N('ironIn', 140, 0), N('mazeIn', 160, 0),
  N('well', 200, -18.8), N('whiteGateOut', 200, 12),
  N('ladder', -40, 561), N('chasmW', -25, 560), N('chasmE', -17, 560), N('aEast', 48, 560), N('bSouth', 48, 662), N('dWest', 0, 662), N('forecourt', 0, 652), N('blackDais', 0, 636.9),
];
const EDGES: [string, string][] = [
  ['spawn', 'circle'], ['spawn', 'gateOut'], ['gateOut', 'goldDais'], ['spawn', 'hubW'], ['spawn', 'keepE'], ['keepE', 'keepNE'], ['keepNE', 'hubN'], ['spawn', 'hubE'], ['circle', 'hubE'], ['circle', 'keepE'],
  ['hubW', 'corrW'], ['corrW', 'shore'], ['shore', 'wade1'], ['wade1', 'nearIsland'], ['nearIsland', 'wade2'], ['wade2', 'wade3'], ['wade3', 'neckE'], ['wade1', 'wade2'], ['neckE', 'neckW'], ['neckW', 'farBank'], ['farBank', 'farIsland'], ['farIsland', 'shrine'],
  ['hubN', 'corrN'], ['corrN', 'dunesIn'], ['dunesIn', 'whiteKey'], ['dunesIn', 'cageOut'], ['whiteKey', 'cageOut'],
  ['hubE', 'corrE'], ['corrE', 'ironIn'], ['ironIn', 'mazeIn'], ['mazeIn', 'well'], ['mazeIn', 'whiteGateOut'],
  ['well', 'ladder'], ['ladder', 'chasmW'], ['chasmW', 'chasmE'], ['chasmE', 'aEast'], ['aEast', 'bSouth'], ['bSouth', 'dWest'], ['dWest', 'forecourt'], ['forecourt', 'blackDais'],
];

const TELEGRAPH: Record<BeastKind, number> = { eel: 0.6, scarab: 0.5, stag: 0.7, wyrm: 0.4 };

interface Step { name: string; update(dt: number): boolean }

export class RouteBot {
  running = false;
  private steps: Step[] = [];
  private i = 0;
  private stuckT = 0;
  private lastPos = new Vec3();
  private unstickT = 0;
  private unstickDir = 1;
  log: string[] = [];
  elapsed = 0;
  /** Debug: the active plan and steering target. */
  curPlan: Vec3[] = [];
  curWp: Vec3 | null = null;
  curWant = 0;
  private dodgeCd = 0;

  constructor(private readonly g: Game) {}

  private say(s: string): void { this.log.push(`${this.elapsed.toFixed(0)}s ${s}`); if (this.log.length > 200) this.log.shift(); }

  // ---------- navigation ----------
  private nearestNode(p: Vec3, restrictTo?: Set<string>): Node {
    let best = NODES[0], bd = Infinity;
    for (const n of NODES) {
      if (restrictTo && !restrictTo.has(n.id)) continue;
      const d = n.p.distanceToXZ(p);
      if (d < bd) { best = n; bd = d; }
    }
    return best;
  }
  private route(fromId: string, toId: string): Node[] {
    const adj = new Map<string, string[]>();
    for (const [a, b] of EDGES) { (adj.get(a) ?? adj.set(a, []).get(a)!).push(b); (adj.get(b) ?? adj.set(b, []).get(b)!).push(a); }
    const prev = new Map<string, string | null>([[fromId, null]]);
    const q = [fromId];
    while (q.length) {
      const c = q.shift()!;
      if (c === toId) break;
      for (const n of adj.get(c) ?? []) if (!prev.has(n)) { prev.set(n, c); q.push(n); }
    }
    if (!prev.has(toId)) return [];
    const out: Node[] = [];
    for (let c: string | null = toId; c; c = prev.get(c) ?? null) out.push(NODES.find((n) => n.id === c)!);
    return out.reverse();
  }

  /** Walk to a world point: through the node graph (and the maze BFS inside Ironwood), then straight. */
  private goto(name: string, target: () => Vec3, reach = 1.6): Step {
    let waypoints: Vec3[] | null = null;
    let lastTarget = new Vec3(Infinity, 0, 0);
    const g = this.g;
    return {
      name,
      update: (dt) => {
        const t = target();
        const p = g.player.position;
        if (t.distanceToXZ(lastTarget) > 3 || !waypoints) {
          lastTarget = t.clone();
          waypoints = this.plan(p, t);
          this.say(`${name}: plan ${waypoints.length} wps`);
        }
        // a teleport (well ↔ ladder) invalidates the plan
        if (waypoints.length && waypoints[0].distanceToXZ(p) > 120) { waypoints = this.plan(p, t); this.say(`${name}: re-plan ${waypoints.length} wps`); }
        // advance waypoints
        while (waypoints.length > 1 && waypoints[0].distanceToXZ(p) < 2.2) waypoints.shift();
        const wp = waypoints[0];
        this.curPlan = waypoints; this.curWp = wp;
        if (wp.distanceToXZ(p) < reach && waypoints.length === 1) { this.release(); return true; }
        this.steer(wp, dt);
        return false;
      },
    };
  }

  private plan(from: Vec3, to: Vec3): Vec3[] {
    const g = this.g;
    const inMaze = (p: Vec3) => p.x > 150 && p.x < 250 && Math.abs(p.z) < 50;
    const sameArea = (a: Vec3, b: Vec3) => (a.z > 400) === (b.z > 400);
    const pts: Vec3[] = [];
    if (sameArea(from, to) && from.distanceToXZ(to) < 30 && !inMaze(from) && !inMaze(to)) return [to.clone()];
    const a = this.nearestNode(from), b = this.nearestNode(to);
    const r = this.route(a.id, b.id);
    for (const n of r) pts.push(n.p.clone());
    // expand maze legs with the BFS over cells; maze cell waypoints near the well detour around it
    // (the well node itself stays: reaching it is how we descend)
    const detour = (c: Vec3): Vec3 => (c.distanceToXZ(SPOTS.well) < 3.2 ? new Vec3(SPOTS.well.x - 3.2, 0, SPOTS.well.z) : c);
    const out: Vec3[] = [];
    for (let k = 0; k < pts.length; k++) {
      const cur = pts[k];
      if (k > 0 && inMaze(pts[k - 1]) && inMaze(cur)) {
        const path = g.world.maze.path(g.world.maze.cellOf(pts[k - 1]), g.world.maze.cellOf(cur));
        for (const c of path.slice(1)) out.push(detour(c));
      }
      out.push(cur);
    }
    if (inMaze(to) && out.length && inMaze(out[out.length - 1])) {
      const path = g.world.maze.path(g.world.maze.cellOf(out[out.length - 1]), g.world.maze.cellOf(to));
      for (const c of path.slice(1)) out.push(detour(c));
    }
    out.push(to.clone());
    // drop leading waypoints we are already standing at (including the well node after climbing out)
    while (out.length > 1 && out[0].distanceToXZ(from) < 4) out.shift();
    return out;
  }

  private steer(wp: Vec3, dt: number): void {
    const g = this.g, v = g.input.virtual, p = g.player.position;
    let want = Math.atan2(wp.x - p.x, wp.z - p.z);
    // hazard avoidance: never walk into black water or a pit; hug the bank instead
    const deadly = (a: number, d: number) => {
      const x = p.x + Math.sin(a) * d, z = p.z + Math.cos(a) * d;
      const th = g.statics.terrainHeight(x, z);
      const gr = g.statics.ground(x, p.y + 0.2, z, 0.5);
      const wet = g.world.biome === 'marsh' || g.world.biome === 'cistern';
      return !gr.floor && ((wet && th < MARSH_DEADLY_Y + 0.45) || th < p.y - 3);
    };
    this.curWant = want;
    const remaining = wp.distanceToXZ(p);
    const near = Math.min(1.2, remaining), far = Math.min(2.8, remaining);
    if (deadly(want, near) || deadly(want, far)) {
      let best = want, found = false;
      for (const off of [0.5, -0.5, 1.0, -1.0, 1.5, -1.5, 2.2, -2.2]) {
        if (!deadly(want + off, near) && !deadly(want + off, far)) { best = want + off; found = true; break; }
      }
      want = found ? best : want + Math.PI;
    }
    // turn the camera toward the waypoint (mouse look) with a capped rate so ±π never oscillates, then push W
    const diff = wrapAngle(want - g.tpc.yaw);
    const turn = Math.max(-0.35, Math.min(0.35, diff * 0.6));
    v.mouseDx = -turn / g.tpc.sensitivity;
    if (Math.abs(diff) < 1.3) v.down.add('KeyW'); else v.down.delete('KeyW');
    if (Math.abs(diff) < 0.6 && g.world.biome !== 'cistern') v.down.add('ShiftLeft'); else v.down.delete('ShiftLeft');
    // stuck detection: wiggle with a dodge and a sidestep
    if (p.distanceToXZ(this.lastPos) < 0.25 * dt * 60 * 0.02) this.stuckT += dt; else this.stuckT = 0;
    this.lastPos.copy(p);
    if (this.unstickT > 0) {
      this.unstickT -= dt;
      v.down.delete('KeyW');
      v.down.add(this.unstickDir > 0 ? 'KeyD' : 'KeyA');
      v.down.add('KeyS');
    } else { v.down.delete('KeyA'); v.down.delete('KeyD'); v.down.delete('KeyS'); }
    if (this.stuckT > 0.8) {
      this.stuckT = 0;
      this.unstickT = 0.5;
      this.unstickDir = -this.unstickDir;
      v.pressed.add('Space');
      this.say('unstick');
    }
  }
  private release(): void {
    const v = this.g.input.virtual;
    v.down.clear();
  }

  private press(key: string, name = key): Step {
    let done = false;
    return { name, update: () => { if (!done) { this.g.input.virtual.pressed.add(key); done = true; return false; } return true; } };
  }
  private wait(seconds: number, name = 'wait'): Step {
    let t = 0;
    return { name, update: (dt) => { t += dt; return t >= seconds; } };
  }
  private face(target: () => Vec3): Step {
    let n = 0;
    return { name: 'face', update: () => { const p = this.g.player.position, t = target(); this.g.player.facing = Math.atan2(t.x - p.x, t.z - p.z); this.g.tpc.yaw = this.g.player.facing; return ++n > 2; } };
  }
  private itemPos(id: ItemId): () => Vec3 {
    return () => { const it = this.g.world.items.get(id)!; return this.g.world.items.worldPos(it).clone(); };
  }
  /** While the bird has it, stand still; it drops loot within two minutes. */
  private airborne(id: ItemId): boolean {
    const it = this.g.world.items.get(id)!;
    if (it.loc !== 'magpie') return false;
    this.release();
    return true;
  }
  private spot(id: string): () => Vec3 { const n = NODES.find((x) => x.id === id)!; return () => n.p; }
  /** Stand off a beast with the sword: dodge its telegraph, hit it while it recovers. Re-fetches the sword after a death. */
  private fight(kind: BeastKind): Step {
    return { name: 'fight ' + kind, update: (dt) => {
      const g = this.g, v = g.input.virtual, p = g.player.position;
      const b = g.world.beasts.find((x) => x.kind === kind);
      if (!b || !b.alive) { this.release(); return true; }
      if (!g.world.items.isHolding('sword')) { this.steps.splice(this.i + 1, 0, ...this.take('sword'), this.fight(kind)); this.release(); return true; }
      const d = b.position.distanceToXZ(p);
      const want = Math.atan2(b.position.x - p.x, b.position.z - p.z);
      v.mouseDx = -Math.max(-0.35, Math.min(0.35, wrapAngle(want - g.tpc.yaw) * 0.6)) / g.tpc.sensitivity;
      g.player.facing = want;
      v.down.delete('ShiftLeft');
      const vulnerable = b.state === 'recover' || b.state === 'stagger';
      if (d > (vulnerable ? 2.4 : 5.5)) { v.down.add('KeyW'); v.down.delete('KeyS'); }
      else if (d < 3.5 && !vulnerable) { v.down.delete('KeyW'); v.down.add('KeyS'); }
      else { v.down.delete('KeyW'); v.down.delete('KeyS'); }
      if (vulnerable && d < 3.0 && !g.world.combat.attacking) v.pressed.add('KeyJ');
      void dt;
      return false;
    } };
  }

  /** Walk to an item wherever it is now and take it (or swap for it). */
  private take(id: ItemId): Step[] {
    const go = this.goto('to ' + id, this.itemPos(id), 1.5);
    const guarded: Step = { name: go.name, update: (dt) => (this.airborne(id) ? false : go.update(dt)) };
    return [guarded, this.press('KeyE', 'take ' + id), this.wait(0.3)];
  }
  private placePlank(spotId: string, gap: Vec3): Step[] {
    return [this.goto('plank to ' + spotId, this.spot(spotId), 0.7), this.wait(0.4, 'settle'), this.face(() => gap), this.press('KeyF', 'place plank'), this.wait(0.5)];
  }
  /** Walk into the well; done once we are standing in the Cistern. */
  private descend(): Step {
    const go = this.goto('descend', this.spot('well'), 0.5);
    return { name: 'descend', update: (dt) => {
      const g = this.g;
      if (g.world.biome === 'cistern') { this.release(); return g.player.state !== 'frozen'; }
      go.update(dt);
      return false;
    } };
  }
  /** Walk into the ladder; done once we are back in Ironwood. */
  private ascend(): Step {
    const go = this.goto('ascend', () => new Vec3(-40, 0, 557.4), 0.5);
    return { name: 'ascend', update: (dt) => {
      const g = this.g;
      if (g.world.biome === 'ironwood') { this.release(); return g.player.state !== 'frozen'; }
      go.update(dt);
      return false;
    } };
  }

  /** Detour to the stone circle when hurt (only worth it in the Hearth). */
  private healIfNeeded(): Step {
    return { name: 'heal?', update: () => {
      if (this.g.player.hearts >= 3) return true;
      this.steps.splice(this.i + 1, 0, this.goto('to circle', this.spot('circle'), 2.0), this.wait(2.6, 'heal'));
      return true;
    } };
  }
  private ensureHolding(id: ItemId): Step {
    // if the magpie took it, go get it again
    return { name: 'ensure ' + id, update: () => {
      if (this.g.world.items.isHolding(id)) return true;
      const it = this.g.world.items.get(id)!;
      if (it.loc === 'magpie') { this.say(id + ' stolen; waiting'); return false; }
      this.steps.splice(this.i + 1, 0, ...this.take(id));
      return true;
    } };
  }

  /** The critical path (§6), ordered to minimise trips. */
  runCriticalPath(): void {
    const g = this.g;
    const neck = new Vec3(-218, 0, -4);
    const chasm = new Vec3(SPOTS.cisternChasm.x, 0, 560);
    this.steps = [
      ...this.take('sword'),
      this.goto('to the eel', this.spot('wade3'), 2.0),
      this.fight('eel'),
      ...this.take('plank'),                                   // swap sword for plank on the near island
      this.ensureHolding('plank'),
      ...this.placePlank('neckE', neck),
      this.goto('cross neck', this.spot('neckW'), 1.2),
      ...this.take('goldKey'),
      this.goto('back over neck', this.spot('neckE'), 1.2),
      this.ensureHolding('goldKey'),
      this.goto('gold gate', this.spot('gateOut'), 1.0),
      this.wait(2.8, 'gate opens'),
      this.healIfNeeded(),
      ...this.take('magnet'),
      this.goto('white key spot', this.spot('whiteKey'), 1.5), this.press('KeyF', 'pulse'), this.wait(1.2),
      this.goto('cage', this.spot('cageOut'), 1.2), this.press('KeyF', 'pulse'), this.wait(1.2),
      ...this.take('blackKey'),                                // swap magnet for black key
      this.ensureHolding('blackKey'),
      this.goto('black key to ladder', this.spot('ladder'), 1.5),
      this.press('KeyE', 'drop black key'), this.wait(0.5),
      this.ascend(),
      ...this.take('plank'),
      this.ensureHolding('plank'),
      this.descend(),
      this.ensureHolding('plank'),
      ...this.placePlank('chasmW', chasm),
      ...this.take('blackKey'),
      this.goto('cross chasm', this.spot('chasmE'), 1.0),
      this.goto('black gate', this.spot('forecourt'), 1.0),
      this.wait(2.8, 'gate opens'),
      ...this.take('chalice'),
      this.ensureHolding('chalice'),
      this.goto('chalice to chasm', this.spot('chasmE'), 1.0),
      this.goto('over chasm', this.spot('chasmW'), 1.0),
      this.ascend(),
      this.ensureHolding('chalice'),
      this.goto('home', this.spot('goldDais'), 1.5),
    ];
    this.i = 0;
    this.running = true;
    this.elapsed = 0;
    g.input.useVirtual = true;
    this.say('route bot started');
  }

  stop(): void { this.running = false; this.release(); this.g.input.useVirtual = false; }

  update(dt: number): void {
    if (!this.running) return;
    this.elapsed += dt;
    const g = this.g;
    if (g.world.won) { this.say('WON'); this.stop(); return; }
    if (g.player.state === 'dead' || g.player.state === 'frozen') { this.release(); return; }
    // heal opportunistically when passing the circle
    if (g.player.hearts < 3 && g.player.position.distanceToXZ(SPOTS.stoneCircle) < 5) { this.release(); return; }
    // dodge reflex: roll when a nearby beast telegraphs or lunges
    this.dodgeCd -= dt;
    const p = g.player.position;
    const threat = g.world.beasts.find((b) => b.alive && b.position.distanceToXZ(p) < 8 && ((b.state === 'telegraph' && b.stateT > TELEGRAPH[b.kind] - 0.3) || (b.state === 'attack' && b.stateT < 0.08)));
    if (threat && this.dodgeCd <= 0 && g.player.grounded && g.player.state !== 'dodge') { g.input.virtual.pressed.add('Space'); this.dodgeCd = 0.9; this.say('dodge ' + threat.kind); }
    const step = this.steps[this.i];
    if (!step) { this.stop(); return; }
    if (step.update(dt)) { this.say('done: ' + step.name); this.i++; this.release(); }
  }
  get current(): string { return this.steps[this.i]?.name ?? '-'; }
}
